import { Component, inject, OnInit } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { first, Observable, Subscription } from 'rxjs';
import { LocationStrategy, AsyncPipe, NgClass } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../services/auth/auth.service';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ResetPasswordRequest } from '../../../interfaces/auth/forgot-password.model';

enum ErrorStates {
  NotSubmitted,
  HasError,
  NoError,
}

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
    InputTextModule,
    ButtonModule,
    NgClass,
  ],
})
export class ResetPasswordComponent implements OnInit {
  private fb = inject(FormBuilder);

  resetPasswordForm: FormGroup;
  hasNumber = false;
  hasLowercase = false;
  hasUppercase = false;
  hasNoSpaces = false;
  hasValidLength = false;

  token = '';

  errorState!: ErrorStates;
  isLoading$: Observable<boolean>;

  url = inject(LocationStrategy);
  authService = inject(AuthService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);

  get f() {
    return this.resetPasswordForm.controls;
  }
  private unsubscribe: Subscription[] = [];
  constructor() {
    this.resetPasswordForm = this.fb.group(
      {
        password: [
          '',
          [
            Validators.required,
            Validators.pattern(
              '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])([a-zA-Z0-9!@#$%^&*(),.?":{}|<>]+)$'
            ),
          ],
        ],
        confirmPassword: ['', Validators.required],
      },
      { validator: this.matchPasswords }
    );

    this.isLoading$ = this.authService.isLoading$;
  }
  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['token']) {
        this.token = params['token'];
      } else {
        this.router.navigate(['**']);
      }
    });
  }

  matchPasswords(group: FormGroup) {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { notMatching: true };
  }

  validatePassword(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.hasNumber = /\d/.test(value);
    this.hasLowercase = /[a-z]/.test(value);
    this.hasUppercase = /[A-Z]/.test(value);
    this.hasNoSpaces = !/\s/.test(value);
    this.hasValidLength = value.length >= 8;
  }
  onSubmit() {
    this.errorState = ErrorStates.NotSubmitted;

    const resetPassword: ResetPasswordRequest = {
      newPassword: this.f['password'].value,
      token: this.token,
    };
    const forgotPasswordSubscr = this.authService
      .resetPassword(resetPassword)
      .pipe(first())
      .subscribe({
        next: () => {
          this.errorState = ErrorStates.NoError;
          this.router.navigate(['/auth/login']);
        },
        error: () => {
          this.errorState = ErrorStates.HasError;
        },
      });
    this.unsubscribe.push(forgotPasswordSubscr);
  }
}
